import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert, InputNumber, Input } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getForecasts,
  deleteForecast,
  generateForecast,
  updateForecastActual,
  getForecastAccuracy,
  FORECAST_TYPES,
  FORECAST_METHODS,
  FORECAST_STATUSES,
  SNAPSHOT_ENTITY_TYPES,
  type Forecast,
  type ForecastFilters,
  type ForecastAccuracyResult,
} from '@/api/analyticsApi';
import { useAuth } from '@/hooks/useAuth';
import AnalyticsNav from './AnalyticsNav';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  expired: 'default',
  superseded: 'orange',
};

export default function ForecastListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [forecastTypeFilter, setForecastTypeFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [generating, setGenerating] = useState(false);
  const [genType, setGenType] = useState<string>('revenue');
  const [genEntity, setGenEntity] = useState<string>('overall');
  const [genMethod, setGenMethod] = useState<string>('linear_regression');
  const [genPeriods, setGenPeriods] = useState<number>(3);
  const [actualModal, setActualModal] = useState<{ id: string; visible: boolean }>({ id: '', visible: false });
  const [actualValue, setActualValue] = useState<number>(0);
  const [actualNotes, setActualNotes] = useState<string>('');
  const [savingActual, setSavingActual] = useState(false);

  const canRead = hasPermission('analytics', 'read');
  const canWrite = hasPermission('analytics', 'write');
  const canDelete = hasPermission('analytics', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: ForecastFilters = { ...params };
      if (forecastTypeFilter) filters.forecast_type = forecastTypeFilter;
      if (statusFilter) filters.status = statusFilter;
      return getForecasts(filters);
    },
    [forecastTypeFilter, statusFilter]
  );

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const results = await generateForecast({
        forecast_type: genType,
        entity_type: genEntity,
        method: genMethod,
        periods: genPeriods,
      });
      message.success(t('forecast.generate_success', { count: Array.isArray(results) ? results.length : 1 }));
      gridRef.current?.reload();
    } catch {
      message.error(t('app.error'));
    } finally {
      setGenerating(false);
    }
  }, [genType, genEntity, genMethod, genPeriods, t]);

  const handleUpdateActual = useCallback(async () => {
    setSavingActual(true);
    try {
      await updateForecastActual(actualModal.id, {
        actual_value: actualValue,
        notes: actualNotes || undefined,
      });
      message.success(t('forecast.actual_updated'));
      setActualModal({ id: '', visible: false });
      gridRef.current?.reload();
    } catch {
      message.error(t('app.error'));
    } finally {
      setSavingActual(false);
    }
  }, [actualModal.id, actualValue, actualNotes, t]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('forecast.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteForecast(id);
            message.success(t('forecast.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleCheckAccuracy = useCallback(async (forecastType: string) => {
    try {
      const result = await getForecastAccuracy(forecastType);
      Modal.info({
        title: t('forecast.accuracy_title'),
        content: (
          <div>
            <p>{t('forecast.field.forecast_type')}: {t(`forecast.type.${result.forecast_type}`, result.forecast_type)}</p>
            <p>{t('forecast.accuracy_total')}: {result.total_forecasts}</p>
            <p>{t('forecast.accuracy_with_actuals')}: {result.forecasts_with_actuals}</p>
            <p>MAPE: {(result.mean_absolute_percentage_error * 100).toFixed(2)}%</p>
            <p>MAE: {result.mean_absolute_error.toFixed(2)}</p>
          </div>
        ),
      });
    } catch {
      message.error(t('app.error'));
    }
  }, [t]);

  const columns: ColumnsType<Forecast> = useMemo(
    () => [
      {
        title: t('forecast.field.forecast_type'),
        dataIndex: 'forecast_type',
        key: 'forecast_type',
        width: 140,
        render: (v: string) => (
          <Tag color="geekblue">{t(`forecast.type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('forecast.field.entity_type'),
        dataIndex: 'entity_type',
        key: 'entity_type',
        width: 110,
        render: (v: string) => (
          <Tag color="blue">{t(`analytics_snapshot.entity_type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('forecast.field.method'),
        dataIndex: 'method',
        key: 'method',
        width: 150,
        render: (v: string) => t(`forecast.method.${v}`, v),
      },
      {
        title: t('forecast.field.period_start'),
        dataIndex: 'period_start',
        key: 'period_start',
        width: 110,
        render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
      },
      {
        title: t('forecast.field.period_end'),
        dataIndex: 'period_end',
        key: 'period_end',
        width: 110,
        render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
      },
      {
        title: t('forecast.field.forecast_value'),
        dataIndex: 'forecast_value',
        key: 'forecast_value',
        width: 120,
        sorter: true,
        render: (v: number) => v?.toLocaleString() ?? '-',
      },
      {
        title: t('forecast.field.actual_value'),
        dataIndex: 'actual_value',
        key: 'actual_value',
        width: 120,
        render: (v: number | null) => v != null ? v.toLocaleString() : '-',
      },
      {
        title: t('forecast.field.confidence_level'),
        dataIndex: 'confidence_level',
        key: 'confidence_level',
        width: 100,
        render: (v: number) => `${(v * 100).toFixed(0)}%`,
      },
      {
        title: t('forecast.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (v: string) => (
          <Tag color={STATUS_COLORS[v] ?? 'default'}>
            {t(`forecast.status.${v}`, v)}
          </Tag>
        ),
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: Forecast) => (
          <Space size="small">
            {canWrite && record.status === 'active' && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setActualValue(record.actual_value ?? 0);
                  setActualNotes(record.notes ?? '');
                  setActualModal({ id: record.id, visible: true });
                }}
                title={t('forecast.update_actual')}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space wrap>
      <Select
        allowClear
        placeholder={t('forecast.field.forecast_type')}
        value={forecastTypeFilter}
        onChange={(v) => { setForecastTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 160 }}
        options={FORECAST_TYPES.map((ft) => ({
          value: ft,
          label: t(`forecast.type.${ft}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('forecast.field.status')}
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); gridRef.current?.reload(); }}
        style={{ width: 120 }}
        options={FORECAST_STATUSES.map((s) => ({
          value: s,
          label: t(`forecast.status.${s}`),
        }))}
      />
      {canWrite && (
        <Space>
          <Select value={genType} onChange={setGenType} style={{ width: 150 }}
            options={FORECAST_TYPES.map((ft) => ({ value: ft, label: t(`forecast.type.${ft}`) }))}
          />
          <Select value={genEntity} onChange={setGenEntity} style={{ width: 120 }}
            options={SNAPSHOT_ENTITY_TYPES.map((et) => ({ value: et, label: t(`analytics_snapshot.entity_type.${et}`) }))}
          />
          <Select value={genMethod} onChange={setGenMethod} style={{ width: 170 }}
            options={FORECAST_METHODS.map((m) => ({ value: m, label: t(`forecast.method.${m}`) }))}
          />
          <InputNumber min={1} max={12} value={genPeriods} onChange={(v) => setGenPeriods(v ?? 3)} style={{ width: 70 }} />
          <Button type="primary" icon={<PlusOutlined />} loading={generating} onClick={handleGenerate}>
            {t('forecast.generate')}
          </Button>
        </Space>
      )}
      {canRead && (
        <Select
          placeholder={t('forecast.check_accuracy')}
          style={{ width: 180 }}
          value={undefined}
          onSelect={(v) => {
            if (v) void handleCheckAccuracy(v);
          }}
          options={FORECAST_TYPES.map((ft) => ({
            value: ft,
            label: `${t('forecast.check_accuracy')}: ${t(`forecast.type.${ft}`)}`,
          }))}
        />
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AnalyticsNav />
      <h2>{t('forecast.title')}</h2>
      <DataGrid<Forecast>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('forecast.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
      <Modal
        open={actualModal.visible}
        title={t('forecast.update_actual')}
        onOk={handleUpdateActual}
        onCancel={() => setActualModal({ id: '', visible: false })}
        confirmLoading={savingActual}
      >
        <div style={{ marginBottom: 16 }}>
          <label>{t('forecast.field.actual_value')}</label>
          <InputNumber
            value={actualValue}
            onChange={(v) => setActualValue(v ?? 0)}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>
        <div>
          <label>{t('forecast.field.notes')}</label>
          <Input.TextArea
            value={actualNotes}
            onChange={(e) => setActualNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            style={{ marginTop: 4 }}
          />
        </div>
      </Modal>
    </div>
  );
}
